import { PairName } from '@ui/assets/config/trade-pair-listings';
import { TooltipWithTouch } from '@ui/components/TooltipWithTouch';
import { useDataChainId } from '@ui/hooks/datachain';
import { useCountdown } from '@ui/hooks/useCountdown';
import { Portal } from '@ui/lib/park/portal';
import { Text } from '@ui/lib/park/ui/text';
import { parseSchedule } from '@ui/utils/parseSchedule';
import { useMemo } from 'react';
import { HStack, styled,VStack } from 'styled-system/jsx';

import { usePairMarketScheduleStore } from '../hooks/usePairMarketScheduleStore/usePairMarketScheduleStore';

export const MarketTimeIndicator = ({ pairName }: { pairName: PairName }) => {
  const chainId = useDataChainId();
  const pairSchedule = usePairMarketScheduleStore((s) =>
    s.getMarketScheduleByPair(pairName, chainId),
  );

  const isOpen = !!pairSchedule.isClientOpen;
  const nextTime = isOpen ? pairSchedule.nextClose : pairSchedule.nextOpen;
  const { minStr } = useCountdown({ to: nextTime });

  const { timezone } = useMemo(
    () => parseSchedule(pairSchedule.schedule),
    [pairSchedule.schedule],
  );

  const tzFormatted = useMemo(
    () => formatTimezoneWithOffset(timezone),
    [timezone],
  );

  return (
    <TooltipWithTouch.Root variant="darkBox" positioning={{ placement: 'top' }}>
      <TooltipWithTouch.Trigger asChild>
        <styled.span
          width="6px"
          height="6px"
          borderRadius="50%"
          bgColor={isOpen ? 'system.success' : 'system.error'}
          cursor="help"
        />
      </TooltipWithTouch.Trigger>
      <Portal>
        <TooltipWithTouch.Positioner>
          <TooltipWithTouch.Content maxW="72" textStyle="text.small">
            <VStack gap={2} alignItems="flex-start">
              <HStack gap={2} alignItems="center">
                <styled.span
                  width="10px"
                  height="10px"
                  borderRadius="50%"
                  bgColor={isOpen ? 'system.success' : 'greyWhite.lightGrey3'}
                />
                <Text
                  textStyle="headings.small"
                  color={isOpen ? 'system.success' : 'greyWhite.lightGrey3'}
                >
                  {isOpen ? 'Market open' : 'Market closed'}
                </Text>
              </HStack>

              <VStack gap={1} alignItems="flex-start">
                <Text>
                  {isOpen
                    ? "All's well — Market is open."
                    : 'Time for a walk — this market is closed.'}
                </Text>
                {nextTime && (
                  <Text fontWeight="700">
                    {isOpen
                      ? `Market closes in ${minStr}.`
                      : `Market opens in ${minStr}.`}
                  </Text>
                )}
              </VStack>

              <Text color="greyWhite.lightGrey3">
                {`Exchange timezone: ${tzFormatted}`}
              </Text>
            </VStack>
          </TooltipWithTouch.Content>
        </TooltipWithTouch.Positioner>
      </Portal>
    </TooltipWithTouch.Root>
  );
};

const formatTimezoneWithOffset = (timezone?: string): string => {
  if (!timezone) return 'UTC';
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    }).formatToParts(now);
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    const city = timezone.split('/').pop()?.replaceAll('_', ' ') || timezone;
    const normalized = offset.replace('GMT', 'UTC');
    
return `${city} (${normalized})`;
  } catch {
    return timezone;
  }
};
